import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NUMBER_FORMAT_CONSTANTS } from './../../shared/constants/number-format-constants';

import { v4 as UUID } from 'uuid';
import { Color } from '../../models';

import { ColumnTypes, ControlTypes, FieldTypes, GridFieldTypes, ObjectProperties, SelectOptions } from '.';
import { DECIMAL_TOOLTIP_MESSAGE_CONSTANTS, DESIGN_CONSTANTS, FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS } from '../../shared/constants';
import { FieldType, FieldTypePrimitive, NumberFormatFieldNames, NumberFormatOptions, WafPrefix } from '../../shared/enums';
import { NumberFormattingUtil } from '../../shared/utils';
import { Column, Field, FieldProperty, Options, Section } from './models';

@Injectable({
    providedIn: 'root',
})
export class SmartsFormBuilderLogicService {
    private _indexedColumnCount = 0;
    private _indexedFieldCount = 0;
    private _listOfColumns: Column[] = [];
    private _listOfFieldProperty: Field[] = [];
    private _publishedColumnIds = new Set<string>();
    private _publishedFieldIds = new Set<string>();
    private _publishedTextBoxFieldIds = new Set<string>();
    private _publishedTextColumnIds = new Set<string>();

    sectionsIds: string[] = new Array<string>();
    columnsIds: string[] = new Array<string>();
    fieldsIds: string[] = new Array<string>();
    newGridIds: string[] = new Array<string>();
    changedGridIds: string[] = new Array<string>();

    get listOfColumns(): Column[] {
        return this._listOfColumns;
    }

    set listOfColumns(columns: Column[]) {
        this._listOfColumns = columns ?? [];
        this.indexColumns();
    }

    get listOfFieldProperty(): Field[] {
        return this._listOfFieldProperty;
    }

    set listOfFieldProperty(fields: Field[]) {
        this._listOfFieldProperty = fields ?? [];
        this.indexFields();
    }

    constructor(private _formBuilder: UntypedFormBuilder) { }

    isGroup(control: AbstractControl): control is UntypedFormGroup {
        return !this.isArray(control) && !this.isControl(control);
    }

    isControl(control: AbstractControl): control is UntypedFormControl {
        return control instanceof UntypedFormControl && !(control.value instanceof Array);
    }

    isArray(control: AbstractControl): control is UntypedFormArray {
        return control.value !== null && control.value instanceof Array;
    }

    isSection(control: AbstractControl): boolean {
        return this.isGroup(control) && control.value.hasOwnProperty(ObjectProperties.SectionTitle);
    }

    isColumnOrColumnGroup(control: AbstractControl): boolean {
        return this.isGroup(control) && control.value.hasOwnProperty(ObjectProperties.Fields);
    }

    isColumnGroup(control: AbstractControl): boolean {
        return this.isGroup(control) && control.value.hasOwnProperty(ObjectProperties.GroupTitle);
    }

    controlKeys(groupOrArray: UntypedFormArray | UntypedFormGroup): string[] {
        const controlKeys = new Array<string>();

        for (const controlKey in groupOrArray.controls) {
            controlKeys.push(controlKey);
        }

        return controlKeys;
    }

    addColumn(controlKey: string, formGroup: UntypedFormGroup): void {
        const newColumn: UntypedFormGroup = this._formBuilder.group({
            id: [UUID()],
            type: [ColumnTypes.Column],
        });

        newColumn.addControl(ControlTypes.Fields, this._formBuilder.array([]));

        (formGroup.get(controlKey) as UntypedFormArray).push(newColumn);
    }

    addColumnGroup(controlKey: string, formGroup: UntypedFormGroup): void {
        const newColumnGroup: UntypedFormGroup = this._formBuilder.group({
            id: [UUID()],
            type: [ColumnTypes.Group],
        });
        newColumnGroup.addControl(ColumnTypes.GroupTitle, new UntypedFormControl());
        newColumnGroup.addControl(ControlTypes.Fields, this._formBuilder.array([]));

        (formGroup.get(controlKey) as UntypedFormArray).push(newColumnGroup);
    }

    addSectionBeforeAllExistingSections(controlKey: string, formGroup: UntypedFormGroup): void {
        (formGroup.get(controlKey) as UntypedFormArray).insert(0, this.createNewSection());
    }

    addSectionAfterAllExistingSections(controlKey: string, formGroup: UntypedFormGroup): void {
        if ((formGroup.get(controlKey) as UntypedFormArray) === null) {
            formGroup.addControl(ControlTypes.Sections, new UntypedFormArray([]));
        }

        (formGroup.get(controlKey) as UntypedFormArray).push(this.createNewSection());
    }

    createNewSection(): UntypedFormGroup {
        const newSection: UntypedFormGroup = this._formBuilder.group({
            id: [UUID()],
            type: [ColumnTypes.Section],
            SectionTitle: ['', Validators.required],
            Columns: this._formBuilder.array([]),
            Sections: this._formBuilder.array([]),
        });

        return newSection;
    }

    addTextBox(controlKey: string, formGroup: UntypedFormGroup): void {
        const newTextBox = this.createField({ text: FieldTypes.TextBox }, FieldType.TextBox);
        (formGroup.get(controlKey) as UntypedFormArray).push(newTextBox);
    }

    addTextArea(controlKey: string, formGroup: UntypedFormGroup): void {
        const newTextArea = this.createField({ text: FieldTypes.TextArea }, FieldType.TextArea);
        (formGroup.get(controlKey) as UntypedFormArray).push(newTextArea);
    }

    addDateSelect(controlKey: string, formGroup: UntypedFormGroup): void {
        const newDateSelect = this.createField({ text: FieldTypes.DateSelect }, FieldType.DateSelect);
        (formGroup.get(controlKey) as UntypedFormArray).push(newDateSelect);
    }

    addCheckBox(controlKey: string, formGroup: UntypedFormGroup): void {
        const newCheckBox = this.createField({ text: FieldTypes.CheckBox }, FieldType.CheckBox);
        (formGroup.get(controlKey) as UntypedFormArray).push(newCheckBox);
    }

    addDropDown(controlKey: string, formGroup: UntypedFormGroup): void {
        const newDropDown = this.createField({ text: FieldTypes.DropDown }, FieldType.DropDown);
        (formGroup.get(controlKey) as UntypedFormArray).push(newDropDown);
    }

    addPhotoGallery(controlKey: string, formGroup: UntypedFormGroup): void {
        const newPhotoGallery = this.createField({ text: FieldTypes.PhotoGallery }, FieldType.PhotoGallery);
        (formGroup.get(controlKey) as UntypedFormArray).push(newPhotoGallery);
    }

    addUploader(controlKey: string, formGroup: UntypedFormGroup): void {
        const newUploader = this.createField({ text: FieldTypes.Uploader }, FieldType.Uploader);
        (formGroup.get(controlKey) as UntypedFormArray).push(newUploader);
    }

    addGrid(controlKey: string, formGroup: UntypedFormGroup): void {
        const newGrid = this.createField({ text: FieldTypes.Grid }, FieldType.Grid);
        (formGroup.get(controlKey) as UntypedFormArray).push(newGrid);
    }

    addNumber(controlKey: string, formGroup: UntypedFormGroup): void {
        const newNumber = this.createField({ text: FieldTypes.Number }, FieldType.Number);
        (formGroup.get(controlKey) as UntypedFormArray).push(newNumber);
    }

    addReadOnlyText(controlKey: string, formGroup: UntypedFormGroup): void {
        const newReadOnlyText = this.createField({ text: FieldTypes.ReadOnlyText }, FieldType.ReadOnlyText);
        (formGroup.get(controlKey) as UntypedFormArray).push(newReadOnlyText);
    }

    addReadOnlyTextArea(controlKey: string, formGroup: UntypedFormGroup): void {
        const newReadOnlyTextArea = this.createField({ text: FieldTypes.ReadOnlyTextArea }, FieldType.ReadOnlyTextArea);
        (formGroup.get(controlKey) as UntypedFormArray).push(newReadOnlyTextArea);
    }

    addURLHyperlink(controlKey: string, formGroup: UntypedFormGroup): void {
        const newURLHyperlink = this.createField({ text: FieldTypes.URLHyperlink }, FieldType.URLHyperlink);
        (formGroup.get(controlKey) as UntypedFormArray).push(newURLHyperlink);
    }

    addEmailHyperlink(controlKey: string, formGroup: UntypedFormGroup): void {
        const newEmailHyperlink = this.createField({ text: FieldTypes.EmailHyperlink }, FieldType.EmailHyperlink);
        (formGroup.get(controlKey) as UntypedFormArray).push(newEmailHyperlink);
    }

    addPerson(controlKey: string, formGroup: UntypedFormGroup): void {
        const newPerson = this.createField({ text: FieldTypes.Person }, FieldType.Person);
        (formGroup.get(controlKey) as UntypedFormArray).push(newPerson);
    }

    addFormula(controlKey: string, formGroup: UntypedFormGroup): void {
        const newFormula = this.createField({ text: FieldTypes.Formula }, FieldType.Formula);
        (formGroup.get(controlKey) as UntypedFormArray).push(newFormula);
    }

    createField(fieldText: any = { text: '' }, selectOptions: FieldType): UntypedFormGroup {
        const newField: UntypedFormGroup = this._formBuilder.group({
            id: UUID(),
            Name: this.containsReadOnly(fieldText.text) ? [''] : ['', Validators.required],
            SelectOption: [selectOptions],
            repoId: [null],
            FieldType: this._formBuilder.group({
                id: [selectOptions, Validators.required],
                text: [fieldText.text],
            }),
        });

        const fieldType: UntypedFormGroup = newField.get(ControlTypes.FieldType) as UntypedFormGroup;
        this.getFieldTypeControls(fieldType, selectOptions);
        return newField;
    }

    getFieldTypeControls(
        formGroup: UntypedFormGroup,
        sectionOptions: string,
        type = '',
        enumerator: string[] = [],
        options: Options = this.genericOptions()
    ): UntypedFormGroup {
        switch (sectionOptions) {
            case FieldType.TextBox:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                break;
            case FieldType.TextArea:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        multi: [options.multi],
                    })
                );
                break;
            case FieldType.DateSelect:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        format: [options.format],
                        dateFormat: [options.dateFormat],
                        dateSaveFormat: [options.dateSaveFormat],
                    })
                );
                break;
            case FieldType.CheckBox:
                type = SelectOptions.Bool;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        toggle: [options.toggle],
                    })
                );
                break;
            case FieldType.DropDown:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(SelectOptions.Enum, this._formBuilder.array(enumerator, Validators.required));
                break;
            case FieldType.PhotoGallery:
                formGroup.addControl(SelectOptions.Photos, this._formBuilder.control(true));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        height: [options.height],
                        arrows: [options.arrows],
                        endless: [options.endless],
                    })
                );
                break;
            case FieldType.Uploader:
                formGroup.addControl(SelectOptions.Upload, this._formBuilder.control(true));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        allowedExtensions: this._formBuilder.array(options.allowedExtensions),
                        maxFileSize: [options.maxFileSize],
                    })
                );
                break;
            case FieldType.Grid:
                options.columns.push(this.createIdentifierColumn());
                formGroup.addControl(SelectOptions.Grid, this._formBuilder.control(true));
                formGroup.addControl(SelectOptions.EnableSubRow, this._formBuilder.control(false));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        columns: this._formBuilder.array(
                            options.columns.map((column: Column) => {
                                const temporaryColumn = this._formBuilder.group({
                                    id: [column.id],
                                    order: [column.order],
                                    header: [column.header, Validators.required],
                                    type: [column.type, Validators.required],
                                });
                                if (column.enum) {
                                    temporaryColumn.addControl(
                                        SelectOptions.Enum,
                                        this._formBuilder.array(column.enum)
                                    );
                                }
                                return temporaryColumn;
                            })
                        ),
                    })
                );
                break;
            case FieldType.Number:
                options.format = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.NUMBER;
                type = SelectOptions.Number;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        format: [options.format],
                        decimalPlaces: [options.decimalPlaces || NUMBER_FORMAT_CONSTANTS.defaultDecimalPlace],
                    })
                );
                break;
            case FieldType.ReadOnlyText:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        readOnly: true,
                        displayText: [options.displayText, Validators.required],
                        displayColor: [
                            new Color(
                                options.displayColor.r,
                                options.displayColor.g,
                                options.displayColor.b,
                                options.displayColor.a
                            ),
                            Validators.required,
                        ],
                        bold: [options.bold],
                        italic: [options.italic],
                        size: [options.size],
                        alignment: [options.alignment],
                    })
                );
                break;
            case FieldType.ReadOnlyTextArea:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(
                    SelectOptions.Options,
                    this._formBuilder.group({
                        readOnly: true,
                        displayText: [options.displayText, Validators.required],
                        multi: [options.multi],
                    })
                );
                break;
            case FieldType.URLHyperlink:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                break;
            case FieldType.EmailHyperlink:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                break;

            case FieldType.Person:
                type = SelectOptions.String;
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                break;
            case FieldType.Formula:
                type = SelectOptions.Number;
                options.format = NumberFormatOptions.Number;
                options.decimalPlaces = -1;
                options.richTextFormula = '';
                formGroup.addControl(SelectOptions.Type, this._formBuilder.control(type));
                formGroup.addControl(SelectOptions.Options, this._formBuilder.group({
                    format: [options.format],
                    decimalPlaces: [options.decimalPlaces],
                    richTextFormula: [options.richTextFormula, Validators.required],
                    description: [options.description],
                }));
                break;
            default:
                break;
        }

        return formGroup;
    }

    genericOptions(): Options {
        const genericOptions: Options = {
            alignment: 'Center',
            allowedExtensions: [],
            arrows: true,
            bold: true,
            columns: [],
            dateFormat: FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE_FORMAT,
            dateSaveFormat: FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE_SAVE_FORMAT,
            decimalPlaces: NUMBER_FORMAT_CONSTANTS.defaultDecimalPlace,
            displayColor: new Color(66, 69, 83, 1),
            displayText: '',
            endless: true,
            format: FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE,
            height: DESIGN_CONSTANTS.PHOTO_GALLERY_DEFAULT_HEIGHT,
            italic: false,
            maxFileSize: 100000000,
            multi: true,
            readOnly: false,
            size: '1.6rem',
            toggle: true,
            description: '',
        };
        return genericOptions;
    }

    isFieldTypeReadOnlyText(fieldId: string): boolean {
        return this.compareFieldTypeToKey(fieldId, 'ReadOnlyText');
    }

    isFieldTypeReadOnlyTextArea(fieldId: string): boolean {
        return this.compareFieldTypeToKey(fieldId, 'ReadOnlyTextArea');
    }

    isFieldTypeAReadOnlyField(fieldId: string): boolean {
        return this.isFieldTypeReadOnlyText(fieldId) || this.isFieldTypeReadOnlyTextArea(fieldId);
    }

    compareFieldTypeToKey(fieldId: string, key: string): boolean {
        return fieldId === FieldType[key];
    }

    containsReadOnly(text: string): boolean {
        return text.toLowerCase().includes('read only');
    }

    textBoxField(): FieldProperty {
        const textBox = new FieldProperty();
        textBox.id = FieldType.TextBox;
        textBox.text = FieldTypes.TextBox;
        textBox.type = FieldTypePrimitive.String;

        return textBox;
    }

    textAreaField(): FieldProperty {
        const textArea = new FieldProperty();
        textArea.id = FieldType.TextArea;
        textArea.text = FieldTypes.TextArea;
        textArea.type = FieldTypePrimitive.String;
        textArea.options = new Options();
        textArea.options.multi = true;

        return textArea;
    }

    dateSelectField(): FieldProperty {
        const dateSelect = new FieldProperty();
        dateSelect.id = FieldType.DateSelect;
        dateSelect.text = FieldTypes.DateSelect;
        dateSelect.type = FieldTypePrimitive.String;
        dateSelect.options = new Options();
        dateSelect.options.format = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE;
        dateSelect.options.dateFormat = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE_FORMAT;
        dateSelect.options.dateSaveFormat = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE_SAVE_FORMAT;

        return dateSelect;
    }

    checkBoxField(): FieldProperty {
        const checkBox = new FieldProperty();
        checkBox.id = FieldType.CheckBox;
        checkBox.text = FieldTypes.CheckBox;
        checkBox.type = FieldTypePrimitive.Boolean;
        checkBox.options = new Options();
        checkBox.options.toggle = true;

        return checkBox;
    }

    dropDownListField(): FieldProperty {
        const dropDownList = new FieldProperty();
        dropDownList.id = FieldType.DropDown;
        dropDownList.text = FieldTypes.DropDown;
        dropDownList.type = FieldTypePrimitive.String;
        dropDownList.enum = [];

        return dropDownList;
    }

    photoGalleryField(): FieldProperty {
        const photoGallery = new FieldProperty();
        photoGallery.id = FieldType.PhotoGallery;
        photoGallery.text = FieldTypes.PhotoGallery;
        photoGallery.photos = true;
        photoGallery.options = new Options();
        photoGallery.options.height = DESIGN_CONSTANTS.PHOTO_GALLERY_DEFAULT_HEIGHT;
        photoGallery.options.arrows = true;
        photoGallery.options.endless = true;

        return photoGallery;
    }

    uploaderField(): FieldProperty {
        const uploader = new FieldProperty();
        uploader.id = FieldType.Uploader;
        uploader.text = FieldTypes.Uploader;
        uploader.upload = true;
        uploader.options = new Options();
        uploader.options.allowedExtensions = [''];
        uploader.options.maxFileSize = 100000000;

        return uploader;
    }

    gridField(): FieldProperty {
        const grid = new FieldProperty();
        grid.id = FieldType.Grid;
        grid.text = FieldTypes.Grid;
        grid.grid = true;
        grid.options = new Options();
        grid.options.columns = [];
        grid.enableSubRow = false;

        return grid;
    }

    numberField(): FieldProperty {
        const numberField = new FieldProperty();
        numberField.id = FieldType.Number;
        numberField.text = FieldTypes.Number;
        numberField.type = FieldTypePrimitive.Number;
        numberField.options = new Options();
        numberField.options.format = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.NUMBER;
        numberField.options.decimalPlaces = NUMBER_FORMAT_CONSTANTS.defaultDecimalPlace;

        return numberField;
    }

    readOnlyTextField(): FieldProperty {
        const readOnlyText = new FieldProperty();
        readOnlyText.id = FieldType.ReadOnlyText;
        readOnlyText.text = FieldTypes.ReadOnlyText;
        readOnlyText.menuDisplayText = 'Text';
        readOnlyText.type = FieldTypePrimitive.String;
        readOnlyText.options = new Options();
        readOnlyText.options.readOnly = true;
        readOnlyText.options.bold = true;
        readOnlyText.options.italic = false;

        return readOnlyText;
    }

    readOnlyTextAreaField(): FieldProperty {
        const readOnlyTextArea = new FieldProperty();
        readOnlyTextArea.id = FieldType.ReadOnlyTextArea;
        readOnlyTextArea.text = FieldTypes.ReadOnlyTextArea;
        readOnlyTextArea.menuDisplayText = 'Text Area';
        readOnlyTextArea.type = FieldTypePrimitive.String;
        readOnlyTextArea.options = new Options();
        readOnlyTextArea.options.readOnly = true;
        readOnlyTextArea.options.multi = true;

        return readOnlyTextArea;
    }

    urlHyperlinkField(): FieldProperty {
        const urlHyperlink = new FieldProperty();
        urlHyperlink.id = FieldType.URLHyperlink;
        urlHyperlink.text = FieldTypes.URLHyperlink;
        urlHyperlink.options = new Options();
        urlHyperlink.type = FieldTypePrimitive.String;

        return urlHyperlink;
    }

    emailHyperlinkField(): FieldProperty {
        const emailHyperlink = new FieldProperty();
        emailHyperlink.id = FieldType.EmailHyperlink;
        emailHyperlink.text = FieldTypes.EmailHyperlink;
        emailHyperlink.options = new Options();
        emailHyperlink.type = FieldTypePrimitive.String;

        return emailHyperlink;
    }

    personField(): FieldProperty {
        const person = new FieldProperty();
        person.id = FieldType.Person;
        person.text = FieldTypes.Person;

        return person;
    }

    formulaField(): FieldProperty {
        const formula = new FieldProperty();
        formula.id = FieldType.Formula;
        formula.text = FieldTypes.Formula;
        formula.options = new Options();
        formula.type = FieldTypePrimitive.String;
        return formula;
    }

    fieldProperties(): FieldProperty[] {
        const fieldTypeOptions = new Array<FieldProperty>();

        fieldTypeOptions.push(this.textBoxField());
        fieldTypeOptions.push(this.textAreaField());
        fieldTypeOptions.push(this.dateSelectField());
        fieldTypeOptions.push(this.checkBoxField());
        fieldTypeOptions.push(this.dropDownListField());
        fieldTypeOptions.push(this.photoGalleryField());
        fieldTypeOptions.push(this.uploaderField());
        fieldTypeOptions.push(this.gridField());
        fieldTypeOptions.push(this.numberField());
        fieldTypeOptions.push(this.formulaField());
        fieldTypeOptions.push(this.readOnlyTextField());
        fieldTypeOptions.push(this.readOnlyTextAreaField());
        fieldTypeOptions.push(this.urlHyperlinkField());
        fieldTypeOptions.push(this.emailHyperlinkField());
        fieldTypeOptions.push(this.personField());

        return fieldTypeOptions;
    }

    createSectionssId(): string {
        const uniqueId = UUID();
        this.sectionsIds.push(uniqueId);
        return uniqueId;
    }

    connectedSectionsIds(): string[] {
        return this.sectionsIds;
    }

    removeSectionsId(id: string): void {
        const index = this.sectionsIds.indexOf(id);

        if (index !== -1) {
            this.sectionsIds.splice(index, 1);
        }
    }

    createColumnsId(): string {
        const uniqueId = UUID();
        this.columnsIds.push(uniqueId);
        return uniqueId;
    }

    connectedColumnsIds(): string[] {
        return this.columnsIds;
    }

    removeColumnsId(id: string): void {
        const index = this.columnsIds.indexOf(id);

        if (index !== -1) {
            this.columnsIds.splice(index, 1);
        }
    }

    createFieldsId(): string {
        const uniqueId = UUID();
        this.fieldsIds.push(uniqueId);
        return uniqueId;
    }

    connectedFieldsIds(): string[] {
        return this.fieldsIds;
    }

    removeFieldsId(id: string): void {
        const index = this.fieldsIds.indexOf(id);

        if (index !== -1) {
            this.fieldsIds.splice(index, 1);
        }
    }

    parseData(data: any): UntypedFormArray | UntypedFormGroup | [any] {
        if (Array.isArray(data)) {
            return this._formBuilder.array(data.map((item) => this.parseData(item)));
        }

        if (typeof data === 'object' && data !== null) {
            const formGroupContent = {};
            for (const [key, value] of Object.entries(data)) {
                formGroupContent[key] = this.parseData(value);
            }
            return this._formBuilder.group(formGroupContent);
        }

        return [data];
    }

    validateFormGroup(formGroup: UntypedFormGroup): boolean {
        const fieldTypeId = formGroup.value.FieldType.id;

        if (fieldTypeId !== FieldType.TextBox && fieldTypeId !== FieldType.TextArea) {
            return false;
        }

        this.ensureFieldIndex();

        const fieldId = formGroup.value.id;

        return this._publishedFieldIds.has(fieldId) && this._publishedTextBoxFieldIds.has(fieldId);
    }

    validateFormGroupForGridTypes(gridColumn: UntypedFormGroup): boolean {
        const columnType = gridColumn.value.type;

        if (columnType !== GridFieldTypes.Text && columnType !== GridFieldTypes.TextArea) {
            return false;
        }

        this.ensureColumnIndex();

        const columnId = gridColumn.value.id;

        return this._publishedColumnIds.has(columnId) && this._publishedTextColumnIds.has(columnId);
    }

    isColumnIdPublished(columnId: string): boolean {
        this.ensureColumnIndex();

        return this._publishedColumnIds.has(columnId);
    }

    isFieldIdPublished(fieldId: string): boolean {
        this.ensureFieldIndex();

        return this._publishedFieldIds.has(fieldId);
    }

    addGridColumnFieldsToColumnList(formGroup: UntypedFormGroup): void {
        this.ensureColumnIndex();

        formGroup.value.columns.forEach((column: Column) => {
            if (column.id && this._publishedColumnIds.has(column.id)) {
                return;
            }

            this._listOfColumns.push(column);
            this.indexColumn(column);
            this._indexedColumnCount = this._listOfColumns.length;
        });
    }

    isColumnUnpublished(gridColumn: UntypedFormGroup): boolean {
        if (!gridColumn.value?.type) {
            return true;
        }

        return this.validateFormGroupForGridTypes(gridColumn) || !this.isColumnIdPublished(gridColumn.value.id);
    }

    private ensureColumnIndex(): void {
        if (this._indexedColumnCount !== this._listOfColumns.length) {
            this.indexColumns();
        }
    }

    private ensureFieldIndex(): void {
        if (this._indexedFieldCount !== this._listOfFieldProperty.length) {
            this.indexFields();
        }
    }

    private indexColumn(column: Column): void {
        if (!column?.id) {
            return;
        }

        this._publishedColumnIds.add(column.id);

        if (column.type === GridFieldTypes.Text) {
            this._publishedTextColumnIds.add(column.id);
        }
    }

    private indexColumns(): void {
        this._publishedColumnIds.clear();
        this._publishedTextColumnIds.clear();
        this._listOfColumns.forEach((col: Column) => {
            this.indexColumn(col);
        });
        this._indexedColumnCount = this._listOfColumns.length;
    }

    private indexField(field: Field): void {
        if (!field?.id) {
            return;
        }

        this._publishedFieldIds.add(field.id);

        if (field.FieldType?.id === FieldType.TextBox) {
            this._publishedTextBoxFieldIds.add(field.id);
        }
    }

    private indexFields(): void {
        this._publishedFieldIds.clear();
        this._publishedTextBoxFieldIds.clear();
        this._listOfFieldProperty.forEach((field: Field) => {
            this.indexField(field);
        });
        this._indexedFieldCount = this._listOfFieldProperty.length;
    }

    GetListOfFieldsFromSections(sections: Section[]): Field[] {
        const listOfFields: Field[] = [];
        const processSections = (sections: Section[]): void => {
            sections.forEach(section => {
                section.Columns.forEach(column => {
                    column.Fields.forEach(field => {
                        listOfFields.push(field);
                    });
                });

                if (section.Sections) {
                    processSections(section.Sections);
                }
            });
        };

        processSections(sections);

        return listOfFields;
    }

    generateNewFieldIdsForSection(sections: Section[]): void {
        const fields = this.GetListOfFieldsFromSections(sections);

        fields.forEach(field => {
            field.id = UUID();
        });

        this.AddWafPrefixfToFieldId(fields);
    }

    AddWafPrefixfToFieldId(listOfFields: Field[]): Field[] {

        listOfFields.map(field => {
            switch (field.FieldType.id) {
                case FieldType.URLHyperlink:
                    field.id.includes(WafPrefix.Hyperlink) ? field.id = field.id : field.id = `${WafPrefix.Hyperlink}${field.id}`;
                    break;
                case FieldType.TextBox:
                case FieldType.TextArea:
                    field.id.includes(WafPrefix.RichText) ? field.id = field.id : field.id = `${WafPrefix.RichText}${field.id}`;
                    break;
                default:
                    field.id = field.id;
                    break;
            }
        });

        return listOfFields;
    }

    createIdentifierColumn(): Column {
        const column: Column = new Column();
        column.id = WafPrefix.RichText + UUID();
        column.type = GridFieldTypes.Text;
        column.order = 1;

        return column;
    }

    handleNumberFormatChange(optionsFormGroup: UntypedFormGroup, format: NumberFormatOptions): void {
        const decimalPlacesControl = optionsFormGroup.get(NumberFormatFieldNames.DecimalPlaces);
        if (!decimalPlacesControl) {
            return;
        }

        if (format === NumberFormatOptions.USDollar) {
            decimalPlacesControl.setValue(2);
            decimalPlacesControl.disable();
        }
        else if (NumberFormattingUtil.isMeasureofTime(format)
        ) {
            decimalPlacesControl.setValue(0);
            decimalPlacesControl.disable();
        }
        else if (decimalPlacesControl.disabled) {
            decimalPlacesControl.enable();
        }
    }

    isDecimalPlacesDisabled(optionsFormGroup: UntypedFormGroup): boolean {
        const format = optionsFormGroup?.get(NumberFormatFieldNames.Format)?.value;
        return format === NumberFormatOptions.USDollar || format === NumberFormatOptions.Duration || format === NumberFormatOptions.Hours;
    }

    getDecimalPlacesTooltip(optionsFormGroup: UntypedFormGroup): string {
        const format = optionsFormGroup?.get(NumberFormatFieldNames.Format)?.value;

        switch (format) {
            case NumberFormatOptions.USDollar:
                return DECIMAL_TOOLTIP_MESSAGE_CONSTANTS.USDollarToolTip;
            case NumberFormatOptions.Duration:
                return DECIMAL_TOOLTIP_MESSAGE_CONSTANTS.DurationToolTip;
            case NumberFormatOptions.Hours:
                return DECIMAL_TOOLTIP_MESSAGE_CONSTANTS.HoursToolTip;
            default:
                return '';
        }
    }
}
